/* eslint-disable unicorn/prefer-array-some */
/* eslint-disable no-plusplus */
/* eslint-disable no-unsafe-optional-chaining */
import { ChevronCell, Row } from '@silevis/reactgrid';

const dimensionalCalculateWidth = (totalElements: number, totalWidth: number) => {
  const editField: number = 260;
  const columnsWidth = totalElements * 150 + editField;
  return totalWidth > columnsWidth ? (totalWidth - editField) / totalElements : 150;
};

const calculateWidth = (totalElements: number, totalWidth: number) => {
  const nameField: number = 150;
  const editField: number = 60;
  const columnsWidth = totalElements * 150 + nameField + editField;
  return totalWidth > columnsWidth ? (totalWidth - editField) / totalElements : 150;
};

const dimensionalHeaderRow = (tableProperty_: any) => ({
  rowId: 'header',
  height: 40,
  cells: [
    {
      type: 'header',
      text: 'Name',
      colType: 'header',
      nonEditable: true,
      className: `header-cell header-first`,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any, index: any) => {
          const row = {
            type: 'header',
            colType: 'header',
            nonEditable: true,
            text: data?.name,
            className: `header-cell ${index === tableProperty_?.length - 1 ? ' last-index ' : ''}${
              data?.isActual ? 'actuals-cell' : ''
            }`,
          };
          return row;
        })
      : []),
    {
      type: 'header',
      text: 'Del',
      nonEditable: true,
      colType: 'header',
      className: 'header-cell header-last',
    },
  ],
});

const dimensionalFooterRow = (tableProperty_: any) => ({
  rowId: 'footer',
  height: 40,
  cells: [
    {
      type: 'addRow',
      text: '',
      className: 'footer footer-first',
      showText: true,
      nonEditable: true,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((_: any, index_: number) => {
          const row = {
            type: 'text',
            text: '',
            nonEditable: true,
            className: `${index_ === tableProperty_?.length - 1 ? ' last-index ' : ''} footer`,
            hasFormulaError: false,
          };
          return row;
        })
      : []),
    {
      type: 'text',
      text: '',
      nonEditable: true,
      className: 'footer footer-last',
      hasFormulaError: false,
    },
  ],
});

const getDimensionalRows = (
  tableItemsProperty_: any[],
  tableProperty_: any[],
  modelCurrency?: string,
) => {
  // Memoize header and footer rows since they don't change often
  const headerRow = dimensionalHeaderRow(tableProperty_);
  const footerRow = dimensionalFooterRow(tableProperty_);
  // Build cells separately to improve readability and performance
  const buildCells = (item: any, index: number, properties: any[], currency?: string) => {
    const nameCell = {
      type: item?.chevronType ? 'chevron' : 'text',
      isExpanded: false,
      hasChildren: item?.chevronType,
      parentId: item?.parentId,
      placeholder: 'Enter value',
      currencyType: currency,
      text: item?.name ?? '',
      property: 'name',
      rows: tableItemsProperty_?.length,
      hasFormulaError: item?.warning?.length > 0,
      hasFormulaErrorMessage: item?.warning,
      className: `name ${item?.parentId ? 'chevron-child' : ''} ${
        item?.warning?.length > 0 ? 'formula-error ' : ''
      }`,
      isNewCell: !!item?.isNewCell,
      indicatorType: item?.indicator_type,
      nonEditable: !!item?.parentId,
      index,
      ...item?.cellData,
    };

    const valueCells = properties?.map((data: any, index_: number) => {
      const value = item?.total_values?.find((data_: any) => data_?.name === data?.name);
      return {
        type: 'text',
        isNewCell: !!item?.isNewCell,
        rows: tableItemsProperty_?.length,
        text: item?.isNewCell ? '0' : value ? `${value?.value}` : '0',
        format: item?.type,
        tableName: 'block',
        currencyType: currency,
        index,
        data,
        nonEditable: true,
        className: `${item?.parentId ? ' chevron-child ' : ''} ${
          index_ === properties?.length - 1 ? ' last-index ' : ''
        } ${item?.warning?.length > 0 ? 'formula-error ' : ''} ${
          data?.isActual ? 'actuals-cell' : ''
        }`,
        hasFormulaError: false,
      };
    });

    const deleteCell = {
      type: 'dropdown',
      buttonType: 1,
      currencyType: currency,
      selectOption: false,
      className: `${item?.warning?.length > 0 ? 'formula-error' : ''}`,
      id: item?.id,
      itemName: item?.name,
      nonEditable: !item?.id && !item?.indicatorId,
      values: [{ label: 'Delete row', value: '' }],
    };

    return [nameCell, ...(valueCells || []), deleteCell];
  };
  // Process items in chunks for better performance
  const processItems = (items: any[]) => {
    return items.map((item: any, index: number) => ({
      rowId: item?.id ?? `${item?.name}_${item?.id ?? item?.parentId}`,
      height: 40,
      itemId: item?.id ?? item?.indicatorId,
      itemName: item?.name,
      reorderable: !item?.parentId,
      isNewCell: !!item?.isNewCell,
      cells: buildCells(item, index, tableProperty_, modelCurrency),
    }));
  };

  return [
    headerRow,
    ...(tableItemsProperty_?.length ? processItems(tableItemsProperty_) : []),
    footerRow,
  ];
};

// Add memoization utility
const memoize = <T extends (...parameters: any[]) => any>(targetFunction: T) => {
  const cache = new Map();
  return (...parameters: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(parameters);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = targetFunction(...parameters);
    cache.set(key, result);
    return result;
  };
};

// Optimize getTextWidth with memoization
const getTextWidth = memoize((text: string) => {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  const metrics = context.measureText(text);
  return metrics.width;
});

const getDimensionalColumns = (tableProperty_: any, tableReference: any, plannerBlock?: any) => {
  // Find the maximum width of the content in the "Name" column
  const nameColumnContent = tableProperty_?.map((data: any) => data.name) || [];
  const maxNameWidth = Math.max(...nameColumnContent.map((name: any) => getTextWidth(name)), 250);
  const columnWidth =
    tableProperty_ || tableReference
      ? dimensionalCalculateWidth(tableProperty_?.length, tableReference)
      : 200;
  return [
    {
      columnId: 'name',
      width:
        plannerBlock?.indicator_column_size !== null
          ? plannerBlock?.indicator_column_size
          : maxNameWidth,
      resizable: true,
      reorderable: true,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any) => {
          const row = {
            columnId: `${data?.id}`,
            width: columnWidth,
            resizable: true,
            reorderable: true,
          };
          return row;
        })
      : []),
    { columnId: 'delete', width: 60 },
  ];
};

const headerRow = (tableProperty_: any) => ({
  rowId: 'header',
  height: 40,
  cells: [
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any, index: any) => {
          const row = {
            type: 'header',
            colType: 'header',
            nonEditable: true,
            text: data?.name,
            className: `header-cell ${index === tableProperty_?.length - 1 ? ' last-index ' : ''}`,
          };
          return row;
        })
      : []),
    {
      type: 'header',
      text: 'Del',
      nonEditable: true,
      colType: 'header',
      className: 'header-cell header-last',
    },
  ],
});

const footerRow = (tableProperty_: any) => ({
  rowId: 'footer',
  height: 40,
  cells: [
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any, index: number) => {
          if (index === 0) {
            const row = {
              type: 'addRow',
              text: '',
              showText: true,
              className: 'footer footer-first',
              nonEditable: true,
            };
            return row;
          }
          const row = {
            type: 'text',
            text: '',
            nonEditable: true,
            className: `${index === tableProperty_?.length - 1 ? ' last-index ' : ''} footer`,
            hasFormulaError: false,
          };
          return row;
        })
      : []),
    {
      type: 'text',
      text: '',
      nonEditable: true,
      className: 'footer footer-last',
      hasFormulaError: false,
    },
  ],
});

const getRows = (tableItemsProperty_: any, tableProperty_: any, indicators: any) => {
  return [
    headerRow(tableProperty_),
    ...(tableItemsProperty_?.length
      ? tableItemsProperty_?.map((item: any, index: any) => {
          return {
            rowId: item?.id,
            height: 40,
            itemId: item?.id,
            itemName: item?.['Block indicators'],
            reorderable: true,
            isNewCell: !!item?.isNewCell,
            cells: [
              ...(tableProperty_?.length
                ? tableProperty_?.map((data: any, index_: any) => {
                    if (data?.name === 'Type') {
                      const row = {
                        hasFormulaError: item?.warning?.length > 0,
                        hasFormulaErrorMessage: item?.warning,
                        className: `${
                          index_ === tableProperty_?.length - 1 ? ' last-index ' : ''
                        } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                        type: 'dropdown',
                        nonEditable: !item?.id,
                        selectOption: false,
                        key: 'type',
                        selectedValue: item[`${data?.name}`].toLowerCase(),
                        values: [
                          {
                            label: 'Input',
                            value: 'input',
                          },
                          {
                            label: 'Calculation',
                            value: 'calculation',
                          },
                        ],
                        index,
                        operation: '',
                      };
                      return row;
                    }
                    if (data?.name === 'Format') {
                      const row = {
                        className: `${index_ === tableProperty_?.length - 1 && ' last-index '} ${
                          item?.warning?.length > 0 && 'formula-error '
                        }`,
                        type: 'dropdown',
                        nonEditable: !item?.id,
                        key: 'data_format',
                        selectOption: false,
                        selectedValue: item[`${data?.name}`].toLowerCase(),
                        values: [
                          {
                            label: 'Number',
                            value: 'number',
                          },
                          {
                            label: 'Integer',
                            value: 'integer',
                          },
                          {
                            label: 'Percentage',
                            value: 'percentage',
                          },
                          {
                            label: 'Currency',
                            value: 'currency',
                          },
                        ],
                        index,
                        operation: '',
                      };
                      return row;
                    }
                    if (data?.name === 'Roll up type') {
                      const row = {
                        className: `${
                          index_ === tableProperty_?.length - 1 ? ' last-index ' : ''
                        } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                        type: 'dropdown',
                        nonEditable: !item?.id,
                        selectOption: false,
                        key: 'data_type',
                        selectedValue: item[`${data?.name}`].toLowerCase(),
                        values: [
                          {
                            label: 'Number',
                            value: 'number',
                          },
                          {
                            label: 'Statistic',
                            value: 'statistic',
                          },
                          {
                            label: 'Percentage',
                            value: 'percentage',
                          },
                          {
                            label: 'Ratio',
                            value: 'ratio',
                          },
                          {
                            label: 'Balance',
                            value: 'balance',
                          },
                          {
                            label: 'Opening Balance',
                            value: 'opening_Balance',
                          },
                        ],
                        index,
                        operation: '',
                      };
                      return row;
                    }
                    if (data?.name === 'Default chart type') {
                      const row = {
                        className: `${
                          index_ === tableProperty_?.length - 1 ? ' last-index ' : ''
                        } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                        type: 'dropdown',
                        nonEditable: !item?.id,
                        selectOption: false,
                        key: 'chart_type',
                        selectedValue: item[`${data?.name}`].toLowerCase(),
                        values: [
                          {
                            label: 'Line',
                            value: 'line',
                          },
                          {
                            label: 'Stack',
                            value: 'stack',
                          },
                          {
                            label: 'Bar',
                            value: 'bar',
                          },
                        ],
                        index,
                        operation: '',
                      };
                      return row;
                    }
                    if (data?.name === 'Display as output') {
                      const row = {
                        type: 'checkbox',
                        key: 'favourite',
                        className: `${
                          index_ === tableProperty_?.length - 1 ? ' last-index ' : ''
                        } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                        checked: item[`${data?.name}`],
                      };
                      return row;
                    }
                    if (data?.name === 'Display as sub-total') {
                      const row = {
                        type: 'checkbox',
                        key: 'output_format',
                        className: `${index_ === tableProperty_?.length - 1 ? ' last-index ' : ''}${
                          item?.warning?.length > 0 ? 'formula-error ' : ''
                        }`,
                        extraKey: item[`${data?.name}`],
                        checked: item[`${data?.name}`] === 'subtotal',
                      };
                      return row;
                    }
                    if (data?.name === 'Block indicators') {
                      const row = {
                        type: 'text',
                        rows: tableItemsProperty_?.length,
                        indicatorType: item?.indicator_type,
                        key:
                          data?.name === 'Block indicators'
                            ? 'name'
                            : data?.name === 'Formula'
                            ? 'formula'
                            : data?.name === 'Help text'
                            ? 'help'
                            : '',
                        text: `${item[data?.name]}`,
                        isDraggable: true,
                        property: 'name',
                        isNewCell: !!item?.isNewCell,
                        index,
                        nonEditable: !item?.id && !item?.indicatorId,
                        hasFormulaError: item?.warning?.length > 0,
                        hasFormulaErrorMessage: item?.warning,
                        className: `${
                          index_ === tableProperty_?.length - 1 ? ' last-index ' : ''
                        } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                      };
                      return row;
                    }
                    if (data?.name === 'Formula') {
                      const row = {
                        type: 'formula',
                        text: `${item[data?.name]}`,
                        className: `${
                          index_ === tableProperty_?.length - 1
                            ? ' last-index '
                            : 'disabled cell-disabled'
                        } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                        options: indicators,
                        nonEditable: true,
                      };
                      return row;
                    }
                    const row = {
                      type: 'text',
                      rows: tableItemsProperty_?.length,
                      key:
                        data?.name === 'Block indicators'
                          ? 'name'
                          : data?.name === 'Formula'
                          ? 'formula'
                          : data?.name === 'Help text'
                          ? 'help'
                          : '',
                      text: `${item[data?.name]}`,
                      index,
                      data,
                      nonEditable: !item?.id && !item?.indicatorId,
                      className: `${index_ === tableProperty_?.length - 1 ? ' last-index ' : ''} ${
                        item?.warning?.length > 0 ? 'formula-error ' : ''
                      }`,
                      hasFormulaError: false,
                    };
                    return row;
                  })
                : []),
              {
                type: 'dropdown',
                buttonType: 1,
                selectOption: false,
                id: item?.id,
                className: `${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                itemName: item?.['Block indicators'],
                nonEditable: !item?.id && !item?.indicatorId,
                values: [{ label: 'Delete row', value: '' }],
              },
            ],
          };
        })
      : []),
    footerRow(tableProperty_),
  ];
};

const getColumns = (tableProperty_: any, tableReference: any) => {
  const columnWidth =
    tableProperty_ || tableReference ? calculateWidth(tableProperty_?.length, tableReference) : 200;
  return [
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any) => {
          const row = {
            columnId: `${data?.id}`,
            width: columnWidth,
            resizable: true,
            reorderable: true,
          };
          return row;
        })
      : []),
    { columnId: 'delete', width: 60 },
  ];
};

const findChevronCell = (row: Row) =>
  row.cells.find((cell) => cell.type === 'chevron') as ChevronCell | undefined;

const getExpandedRows = (rows: Row[]): Row[] => {
  if (!rows?.length) return [];

  const expandedRowIds = new Set<string | number>();
  const collapsedParentIds = new Set<string | number>();

  // First pass: identify expanded/collapsed states
  rows.forEach((row) => {
    const chevronCell = findChevronCell(row);
    if (chevronCell) {
      if (chevronCell.isExpanded) {
        expandedRowIds.add(row.rowId!);
      } else {
        collapsedParentIds.add(row.rowId!);
      }
    }
  });

  // Second pass: filter rows
  return rows.filter((row) => {
    const { parentId: initialParentId } = findChevronCell(row) || {};
    if (!initialParentId) return true;

    // Check if any parent is collapsed
    const isParentCollapsed = (startParentId: string | number): boolean => {
      let currentParentId = startParentId;

      const findParentChevron = (id: string | number) => {
        const { rowId } = rows.find((r) => r.rowId === id) || {};
        return rowId ? findChevronCell(rows.find((r) => r.rowId === rowId)!) : undefined;
      };

      while (currentParentId) {
        if (collapsedParentIds.has(currentParentId)) return true;
        const parentChevron = findParentChevron(currentParentId);
        if (!parentChevron?.parentId) break;
        currentParentId = parentChevron.parentId;
      }
      return false;
    };

    return !isParentCollapsed(initialParentId);
  });
};

const buildTree = (rows: Row[]): Row[] => {
  if (!rows?.length) return [];

  // Create Maps for faster lookups
  const rowMap = new Map<string | number, Row>();
  const childrenMap = new Map<string | number, Row[]>();

  // First pass: Build maps
  rows.forEach((row) => {
    if (row.rowId) {
      rowMap.set(row.rowId, row);

      const chevronCell = findChevronCell(row);
      if (chevronCell?.parentId) {
        const parentChildren = childrenMap.get(chevronCell.parentId) || [];
        parentChildren.push(row);
        childrenMap.set(chevronCell.parentId, parentChildren);
      }
    }
  });

  // Second pass: Process chevron cells
  return rows.map((row) => {
    const chevronCell = findChevronCell(row);
    if (chevronCell && !chevronCell.parentId) {
      const rowChildren = childrenMap.get(row.rowId) || [];
      chevronCell.hasChildren = rowChildren.length > 0;

      if (chevronCell.hasChildren) {
        const startIndent = 0;
        const processChildren = (parentId: string | number, level: number) => {
          const nestedChildren = childrenMap.get(parentId) || [];
          nestedChildren.forEach((childRow) => {
            const childChevron = findChevronCell(childRow);
            if (childChevron) {
              childChevron.indent = level;
              childChevron.hasChildren = (childrenMap.get(childRow.rowId) || []).length > 0;
              if (childChevron.hasChildren) {
                processChildren(childRow.rowId!, level + 1);
              }
            }
          });
        };
        processChildren(row.rowId!, startIndent + 1);
      }
    }
    return row;
  });
};

const handleLocationChanged = (event: any, setFocus: any) => {
  setFocus(event);
  return true;
};

// Optimize reorderArray with more efficient array operations
const reorderArray = <T extends { id: number | string }>(
  array: T[],
  indices: number[],
  targetPosition: number,
): (string | number)[] => {
  const movedElements = new Set(indices.map((index) => array[index]));
  const targetIndex =
    Math.min(...indices) < targetPosition
      ? targetPosition + 1
      : targetPosition - indices.filter((index) => index < targetPosition).length;

  const result = array
    .filter((_, index) => !indices.includes(index))
    .reduce((accumulator, item, index) => {
      if (index === targetIndex) {
        return [...accumulator, ...Array.from(movedElements), item];
      }
      return [...accumulator, item];
    }, [] as T[]);

  if (targetIndex >= array.length) {
    result.push(...Array.from(movedElements));
  }

  return result.map((item) => item.id);
};

const handleCanReorderRows = (targetRowId: any) => {
  return typeof targetRowId === 'number';
};

const handleCellChange = (changes: any, columns: any, setRows: any) => {
  setRows((rows_: any) => {
    changes.forEach((change: any) => {
      const changeRowIndex = rows_.findIndex((element: any) => element.rowId === change.rowId);
      const changeColumnIndex = columns.findIndex(
        (element: any) => element.columnId === change.columnId,
      );
      rows_[changeRowIndex].cells[changeColumnIndex] = change?.newCell;
    });
    return [...rows_];
  });
};

const handleColumnResize = (columnId: any, width: number, setColumns: any) => {
  setColumns((column: any) => {
    const columnIndex = column.findIndex((element: any) => element.columnId === columnId);
    const resizedColumn = column[columnIndex];
    const updatedColumn = { ...resizedColumn, width };
    column[columnIndex] = updatedColumn;
    return [...column];
  });
};

export {
  headerRow,
  dimensionalHeaderRow,
  footerRow,
  dimensionalFooterRow,
  getDimensionalRows,
  getRows,
  getDimensionalColumns,
  getColumns,
  buildTree,
  reorderArray,
  getExpandedRows,
  handleCellChange,
  handleColumnResize,
  handleLocationChanged,
  handleCanReorderRows,
};
